import React from 'react';

import {FlexRow, MultiCombobox, WorkSignifier, type ComboboxItem} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import Util from '../../../forecast-app/shared/util/util';

import {DesignSystemMultiComboBoxBorderAdapter} from '../../../forecast-app/shared/components/dropdowns/DesignSystemComboBoxOldLookAdapter';
import {LabelDropdown_labels$data} from './__generated__/LabelDropdown_labels.graphql';

const sortByName = (a, b) => {
	if (!a.id) return 1;
	if (!b.id) return -1;
	return Util.sortAlphabetically(a.name, b.name);
};

export type LabelType = LabelDropdown_labels$data[0];
type LabelDropdownItem = ComboboxItem & LabelType;

type LabelDropdownProps = {
	labels: LabelType[];
	selectedItems: string[];
	setSelectedItems: (labelIds: string[]) => void;
	/**
	 * Input size. Defaults to small, which is what the denser surfaces this was
	 * built for want; a form whose other inputs are the large size passes 'large'
	 * so the label field lines up with them rather than sitting 10px shorter.
	 */
	height?: 'small' | 'large';
	/** Read-only mode: the chips stay legible, the picker will not open. */
	disabled?: boolean;
	'data-cy': string;
};

/* Based on LabelDropdownJS.js. The use case was the project label dropdown so there is no support for suggestions yet*/
export const LabelDropdown = ({
	labels,
	selectedItems,
	setSelectedItems,
	height = 'small',
	disabled,
	'data-cy': dataCy,
}: LabelDropdownProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const sortedLabels = labels.sort(sortByName);

	const dropdownItems: LabelDropdownItem[] = sortedLabels.map(label => {
		return {
			...label,
			key: label.id,
			textValue: label.name || undefined,
			color: label.color || '',
			section: label.category?.id
				? {key: label.category?.id, name: label.category?.name || ''}
				: {key: 'no-category', name: formatMessage({id: 'settings_labels.no_category'})},
		};
	});

	return (
		<FlexRow>
			<DesignSystemMultiComboBoxBorderAdapter>
				<MultiCombobox<LabelDropdownItem>
					height={height}
					width={'flex'}
					items={dropdownItems}
					selectedKeys={selectedItems}
					setSelectedKeys={setSelectedItems}
					disabled={disabled}
					data-cy={dataCy}
				>
					{label => <WorkSignifier workId={label.name || ''} color={label.color || ''} />}
				</MultiCombobox>
			</DesignSystemMultiComboBoxBorderAdapter>
		</FlexRow>
	);
};

export default createFragmentContainer(LabelDropdown, {
	labels: graphql`
		fragment LabelDropdown_labels on Label @relay(plural: true) {
			id
			name
			color
			category {
				id
				name
			}
		}
	`,
});
